import React, { useState, useEffect } from "react";
import PropTypes from 'prop-types';
import { useHistory, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import Breadcrumbs from 'components/Common/Breadcrumb';
import { OfficialHolidaysApi } from "apis/officialHolidays";
import Swal from "../../helpers/swal_helper";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

function AddEditOfficialHoliday(props) {
    const history = useHistory();
    const { id } = useParams();
    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({
        title: "",
        start_date: new Date(),
        end_date: new Date(),
        status: "true"
    });

    const validationSchema = Yup.object().shape({
        title: Yup.string()
            .required(props.t("Title is required"))
            .min(3, props.t("Title must be at least 3 characters"))
            .max(50, props.t("Title must not exceed 50 characters")),
            start_date: Yup.date()
            .required(props.t("Start date is required")),
        end_date: Yup.date()
            .required(props.t("End date is required"))
            .min(Yup.ref('start_date'), props.t("End date must be after start date")),
        status: Yup.boolean()
            .required(props.t("Status is required"))
    });

    useEffect(() => {
        if (id) {
            getOfficialHolidayById(id);
        }
    }, [id]);

    const getOfficialHolidayById = (id) => {
        setLoading(true);
        OfficialHolidaysApi.fetchOfficialHolidayById(id)
            .then((res) => {
                if (res.data.status) {
                    const data = res.data.data;
                    setInitialValues({
                        ...data,
                        start_date: new Date(data.start_date),
                        end_date: new Date(data.end_date),
                        status: data.status
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to fetch official holiday details",
                    icon: "error",
                });
            });
    };

    const handleSubmit = (values, { setSubmitting }) => {
        setLoading(true);

        const submitData = {
            ...values,
            start_date: values.start_date.toISOString(),
            end_date: values.end_date.toISOString(),
            status : values.status === "true"
        };

        const apiCall = id
            ? OfficialHolidaysApi.updateOfficialHoliday({
                ...submitData,
                official_holiday_id: id
            })
            : OfficialHolidaysApi.createOfficialHoliday(submitData);

        apiCall
            .then((res) => {
                if (res.data.status) {
                    Swal.fire({
                        title: "Success",
                        text: res.data.message,
                        icon: "success",
                    }).then(() => {
                        history.push("/official-holidays");
                    });
                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.data.message,
                        icon: "error",
                    });
                }
                setLoading(false);
                setSubmitting(false);
            })
            .catch((err) => {
                console.log(err);
                setLoading(false);
                setSubmitting(false);
                Swal.fire({
                    title: "Error",
                    text: "Failed to save official holiday",
                    icon: "error",
                });
            });
    };

    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs
                    title={props.t("Dashboard")}
                    breadcrumbItem={id ? props.t("Edit Official Holiday") : props.t("Add Official Holiday")}
                />
            </div>

            <div className="container-fluid">
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                <Formik
                                    initialValues={initialValues}
                                    validationSchema={validationSchema}
                                    onSubmit={handleSubmit}
                                    enableReinitialize
                                >
                                    {({ errors, touched, setFieldValue, values }) => (
                                        <Form>
                                            <div className="row mb-3">
                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Title")}</label>
                                                    <Field
                                                        type="text"
                                                        className={`form-control ${errors.title && touched.title ? 'is-invalid' : ''}`}
                                                        name="title"
                                                    />
                                                    {errors.title && touched.title && (
                                                        <div className="invalid-feedback">{errors.title}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Start Date")}</label>
                                                    <DatePicker
                                                        selected={values.start_date}
                                                        onChange={(date) => setFieldValue("start_date", date)}
                                                        showTimeSelect
                                                        dateFormat="Pp"
                                                        className={`form-control ${errors.start_date && touched.start_date ? 'is-invalid' : ''}`}
                                                    />
                                                    {errors.start_date && touched.start_date && (
                                                        <div className="invalid-feedback">{errors.start_date}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("End Date")}</label>
                                                    <DatePicker
  selected={values.end_date}
  onChange={(date) => setFieldValue("end_date", date)}
  showTimeSelect
  dateFormat="Pp"
  className={`form-control ${errors.end_date && touched.end_date ? 'is-invalid' : ''}`}
  minDate={values.start_date}
  popperPlacement="bottom"
  popperModifiers={[
    {
      name: "preventOverflow",
      options: {
        boundary: "viewport"
      }
    }
  ]}
  portalId="root-portal"
/>
                                                    {errors.end_date && touched.end_date && (
                                                        <div className="invalid-feedback">{errors.end_date}</div>
                                                    )}
                                                </div>

                                                <div className="col-md-6">
                                                    <label className="form-label">{props.t("Status")}</label>
                                                    <Field
                                                        as="select"
                                                        className={`form-select ${errors.status && touched.status ? 'is-invalid' : ''}`}
                                                        name="status"
                                                    >
                                                        <option value={true}>{props.t("Active")}</option>
                                                        <option value={false}>{props.t("Inactive")}</option>
                                                    </Field>
                                                    {errors.status && touched.status && (
                                                        <div className="invalid-feedback">{errors.status}</div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="row mb-3">
                                                <div className="col-12">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-primary"
                                                        disabled={loading}
                                                    >
                                                        {loading ? props.t("Saving...") : props.t("Save")}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary ms-2"
                                                        onClick={() => history.push("/official-holidays")}
                                                    >
                                                        {props.t("Cancel")}
                                                    </button>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

AddEditOfficialHoliday.propTypes = {
    t: PropTypes.func,
};

export default withTranslation()(AddEditOfficialHoliday); 